import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  Grid,
  TextField,
  Container,
  Button,
  Typography,
  Box,
  Select,
  InputLabel,
  FormControl,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { UrlApi } from "../../../services/apirest";
import { logEventos } from "../../../services/services";
import { getData } from "../../../services/library";
import moment from "moment";
import "moment/locale/es";

const EntrevistasForm = ({
  props,
  isEditMode,
  onClose,
  obtenerEntrevistas,
  mostrarAlertaTemporal,
  idCandidato,
  puestoQueAplica,
}) => {
  const convertDate = (date) =>
    date ? moment(date).format("YYYY-MM-DD") : null;
  // Estados para campos que afectan la UI
  const [linitVigente, setLinitVigente] = useState(
    isEditMode ? props.linit_vigente : 0
  );
  const [carnetConducirVigente, setCarnetConducirVigente] = useState(
    isEditMode ? props.carnet_conducir_vigente : 0
  );
  const [fetchUsers, setFetchUsers] = useState([]);
  const [tiposDeEntrevista, setTiposDeEntrevista] = useState([]);

  // Fetch de datos
  useEffect(() => {
    getData("/SP_entrevistas/getTipos/entrevista", setTiposDeEntrevista);
    getData("/SP_entrevistas/getUsers/entrevistador", setFetchUsers);
  }, []);

  const user = localStorage.getItem("user");
  const rrhh = localStorage.getItem("adminrrhh");

  const getIdUserLS = (user) => {
    const existingUser = fetchUsers.find((i) => i.username === user);
    return existingUser ? existingUser.id : null;
  };

  const idUser = getIdUserLS(user);

  // Ref principal para los datos del formulario
  const formValuesRef = useRef({
    id: isEditMode ? props.id : undefined,
    id_entrevistador: isEditMode ? props.id_entrevistador : idUser,
    id_candidato: idCandidato,
    fecha_entrevista: isEditMode ? convertDate(props.fecha_entrevista) : "",
    aprobado: isEditMode ? props.aprobado : null,
    observacion: isEditMode ? props.observacion : "",
    caducidad_linit: isEditMode
      ? linitVigente
        ? convertDate(props.caducidad_linit)
        : null
      : null,
    caducidad_carnet: isEditMode
      ? carnetConducirVigente
        ? convertDate(props.caducidad_carnet)
        : null
      : null,
    como_llego_cv: isEditMode ? props.como_llego_cv : "",
    forma_de_comunicarse: isEditMode ? props.forma_de_comunicarse : "",
    tiempo_de_incorporacion: isEditMode ? props.tiempo_de_incorporacion : null,
    algun_conocido: isEditMode ? props.algun_conocido : 0,
    situacion_familiar: isEditMode ? props.situacion_familiar : "",
    remuneracion_pretendida: isEditMode ? props.remuneracion_pretendida : null,
    referencias_laborales: isEditMode ? props.referencias_laborales : "",
    tipo_entrevista_desplegable: isEditMode
      ? props.tipo_entrevista_desplegable
      : "",
  });

  const usuariosOrdenadas = fetchUsers.sort((a, b) =>
    a.nombre.localeCompare(b.nombre)
  );

  // Handlers para campos que afectan la UI
  const handleLinitChange = (e) => {
    const value = e.target.value;
    formValuesRef.current.linit_vigente = value;
    setLinitVigente(value);
    if (value === 0) formValuesRef.current.caducidad_linit = null;
  };

  const handleCarnetChange = (e) => {
    const value = e.target.value;
    formValuesRef.current.carnet_conducir_vigente = value;
    setCarnetConducirVigente(value);
    if (value === 0) formValuesRef.current.caducidad_carnet = null;
  };

  const [entrevistador, setEntrevistador] = useState(
    formValuesRef.current.id_entrevistador ?? getIdUserLS(user) ?? ""
  );

  const fetchUsersMemo = useMemo(() => usuariosOrdenadas, [fetchUsers]);
  const tiposDeEntrevistaMemo = useMemo(
    () => tiposDeEntrevista,
    [tiposDeEntrevista]
  );

  const handleKeyPress = (event) => {
    const allowedChars = /[0-9-\s]/;
    if (!allowedChars.test(event.key)) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    // Asegurarnos de que los usuarios estén cargados
    if (!isEditMode) {
      const idEntrevistador = getIdUserLS(user);
      setEntrevistador(idEntrevistador ?? "");
      formValuesRef.current.id_entrevistador = idEntrevistador ?? "";
    }
  }, [fetchUsers, user]);

  // Submit (adaptado para usar formValuesRef)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = {
      ...formValuesRef.current,
      linit_vigente: linitVigente,
      carnet_conducir_vigente: carnetConducirVigente,
    };

    // Validaciones
    if (
      !formData.id_entrevistador ||
      !formData.fecha_entrevista ||
      formData.observacion.trim() === "" ||
      formData.como_llego_cv.trim() === "" ||
      formData.forma_de_comunicarse.trim() === "" ||
      isNaN(formData.tiempo_de_incorporacion) ||
      formData.tiempo_de_incorporacion === "" ||
      formData.tiempo_de_incorporacion < 0 ||
      formData.tiempo_de_incorporacion === null ||
      formData.situacion_familiar.trim() === "" ||
      !formData.remuneracion_pretendida ||
      formData.referencias_laborales.trim() === "" ||
      !formData.tipo_entrevista_desplegable
    ) {
      mostrarAlertaTemporal("Complete todos los campos obligatorios.", "error");
      return;
    }

    if (puestoQueAplica.toUpperCase() === "CHOFER") {
      if (
        (formData.linit_vigente === 1 && !formData.caducidad_linit) ||
        (formData.carnet_conducir_vigente === 1 && !formData.caducidad_carnet)
      ) {
        mostrarAlertaTemporal(
          "Complete los campos obligatorios para el puesto de chofer.",
          "error"
        );
        return;
      }
    }

    let solicitud = { ...formData, user: localStorage.getItem("user") };
    if (!isEditMode) delete solicitud.id;

    const url = isEditMode
      ? `${UrlApi}/SP_entrevistas/${formData.id}`
      : `${UrlApi}/SP_entrevistas`;
    const method = isEditMode ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(solicitud),
      });

      if (response.ok) {
        mostrarAlertaTemporal(
          isEditMode
            ? "Entrevista actualizada correctamente"
            : "Entrevista creada con éxito",
          "success"
        );
        obtenerEntrevistas();
        onClose();
      } else {
        const error = await response.json();
        mostrarAlertaTemporal(`Error: ${error.message}`, "error");
      }
    } catch (error) {
      console.error("Error en la solicitud:", error);
      mostrarAlertaTemporal("Hubo un problema con la solicitud.", "error");
    }
  };
  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
      <Box sx={{ backgroundColor: "#1F618D", marginBottom: 4 }}>
        <DialogTitle>
          <Typography variant="h6" align="center" color="#fff">
            {isEditMode ? "Editar entrevista" : "Nueva entrevista"}
          </Typography>
        </DialogTitle>
      </Box>
      <Container>
        <Grid container spacing={3}>
          {/* Campo Entrevistador */}
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel>Entrevistador *</InputLabel>
              <Select
                value={entrevistador}
                onChange={(e) => {
                  setEntrevistador(e.target.value);
                  formValuesRef.current.id_entrevistador = e.target.value;
                }}
                label="Entrevistador *"
                disabled={rrhh !== "1"}
              >
                {fetchUsersMemo.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.nombre} {user.apellido}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Campo Tipo de Entrevista */}
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel>Tipo de entrevista *</InputLabel>
              <Select
                defaultValue={formValuesRef.current.tipo_entrevista_desplegable}
                onChange={(e) =>
                  (formValuesRef.current.tipo_entrevista_desplegable =
                    e.target.value)
                }
                label="Tipo de entrevista *"
              >
                {tiposDeEntrevistaMemo.map((tipo) => (
                  <MenuItem key={tipo.id} value={tipo.id}>
                    {tipo.valor}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Campo Fecha de Entrevista */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Fecha de la entrevista *"
              defaultValue={formValuesRef.current.fecha_entrevista}
              onChange={(e) =>
                (formValuesRef.current.fecha_entrevista = e.target.value)
              }
              type="date"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Campo Observación */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Observacion *"
              defaultValue={formValuesRef.current.observacion}
              multiline
              rows={4}
              onChange={(e) =>
                (formValuesRef.current.observacion = e.target.value)
              }
              inputProps={{ maxLength: 1000 }}
            />
            <Typography variant="body2" color="textSecondary">
              {formValuesRef.current.observacion.length} / 1000 caracteres
            </Typography>
          </Grid>

          {/* Campo Como llego el CV */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Como llego el CV"
              defaultValue={formValuesRef.current.como_llego_cv}
              onChange={(e) =>
                (formValuesRef.current.como_llego_cv = e.target.value)
              }
              required
              inputProps={{ maxLength: 50 }}
            />
            <Typography variant="body2" color="textSecondary">
              {formValuesRef.current.como_llego_cv.length} / 50 caracteres
            </Typography>
          </Grid>

          {/* Campo Forma de comunicarse */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Forma de comunicarse"
              defaultValue={formValuesRef.current.forma_de_comunicarse}
              onChange={(e) =>
                (formValuesRef.current.forma_de_comunicarse = e.target.value)
              }
              required
              inputProps={{ maxLength: 100 }}
            />
            <Typography variant="body2" color="textSecondary">
              {formValuesRef.current.forma_de_comunicarse.length} / 100
              caracteres
            </Typography>
          </Grid>

          {/* Campo Referencias laborales */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Referencias laborales"
              defaultValue={formValuesRef.current.referencias_laborales}
              onChange={(e) =>
                (formValuesRef.current.referencias_laborales = e.target.value)
              }
              required
              inputProps={{ maxLength: 100 }}
            />
            <Typography variant="body2" color="textSecondary">
              {formValuesRef.current.referencias_laborales.length} / 100
              caracteres
            </Typography>
          </Grid>

          {/* Campo Situación familiar */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Situación familiar"
              defaultValue={formValuesRef.current.situacion_familiar}
              onChange={(e) =>
                (formValuesRef.current.situacion_familiar = e.target.value)
              }
              required
              inputProps={{ maxLength: 100 }}
            />
            <Typography variant="body2" color="textSecondary">
              {formValuesRef.current.situacion_familiar.length} / 100 caracteres
            </Typography>
          </Grid>

          {/* Campo Tiempo de incorporación */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Tiempo de incorporación (en días)"
              defaultValue={
                formValuesRef.current.tiempo_de_incorporacion || null
              }
              onChange={(e) =>
                (formValuesRef.current.tiempo_de_incorporacion = e.target.value)
              }
              required
              inputProps={{ maxLength: 3 }}
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">días</InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Campo Algún conocido */}
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel>Algún conocido</InputLabel>
              <Select
                defaultValue={formValuesRef.current.algun_conocido}
                onChange={(e) =>
                  (formValuesRef.current.algun_conocido = e.target.value)
                }
                label="Algún conocido"
              >
                <MenuItem value={0}>No</MenuItem>
                <MenuItem value={1}>Sí</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Campo Remuneración pretendida */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Remuneración pretendida"
              defaultValue={formValuesRef.current.remuneracion_pretendida}
              onChange={(e) =>
                (formValuesRef.current.remuneracion_pretendida = e.target.value)
              }
              required
              inputProps={{ maxLength: 17 }}
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">ARS</InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Campos específicos para el puesto de Chofer */}
          {puestoQueAplica.toUpperCase() === "CHOFER" && (
            <>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>¿Linit vigente?</InputLabel>
                  <Select
                    value={linitVigente}
                    onChange={handleLinitChange}
                    label="¿Linit vigente?"
                  >
                    <MenuItem value={0}>No</MenuItem>
                    <MenuItem value={1}>Sí</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>¿Carnet de conducir vigente?</InputLabel>
                  <Select
                    value={carnetConducirVigente}
                    onChange={handleCarnetChange}
                    label="¿Carnet de conducir vigente?"
                  >
                    <MenuItem value={0}>No</MenuItem>
                    <MenuItem value={1}>Sí</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {/* Campo Fecha de caducidad Linit */}
          {linitVigente === 1 && (
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Fecha de caducidad Linit"
                defaultValue={formValuesRef.current.caducidad_linit}
                onChange={(e) =>
                  (formValuesRef.current.caducidad_linit = e.target.value)
                }
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}

          {/* Campo Fecha de caducidad Carnet de conducir */}
          {carnetConducirVigente === 1 && (
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Fecha de caducidad Carnet de conducir"
                defaultValue={formValuesRef.current.caducidad_carnet}
                onChange={(e) =>
                  (formValuesRef.current.caducidad_carnet = e.target.value)
                }
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}

          {/* Botones de acción */}
          <Grid item xs={12} sx={{ textAlign: "right", mb: 2 }}>
            <Button onClick={onClose} color="error">
              Cancelar
            </Button>
            <Button onClick={handleSubmit} color="primary">
              {isEditMode ? "Guardar" : "Agregar"}
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Dialog>
  );
};

export default EntrevistasForm;
